import { linePoints, niceMax, ticks } from '../../lib/chartGeometry'

export interface LineSeries {
  label: string
  values: number[]
  tone: 'amber' | 'faint'
  dashed?: boolean
}

type Props = {
  series: LineSeries[]
  labels: string[]
  height?: number
  ariaLabel: string
}

const W = 760
const LEFT = 44
const RIGHT = 8
const TOP = 8
const BOTTOM = 20
const STROKE = { amber: '#ffb648', faint: '#837e73' } as const

export function LineChart({ series, labels, height = 170, ariaLabel }: Props) {
  const plotW = W - LEFT - RIGHT
  const plotH = height - TOP - BOTTOM
  const max = Math.max(2, niceMax(Math.max(0, ...series.flatMap((s) => s.values))))
  const gridTicks = ticks(max, 3)
  const step = Math.max(1, Math.ceil(labels.length / 6))
  const xAt = (i: number, n: number) => LEFT + (n <= 1 ? plotW / 2 : (i * plotW) / (n - 1))
  const yAt = (v: number) => TOP + plotH - (Math.min(v, max) / max) * plotH

  return (
    <svg role="img" aria-label={ariaLabel} viewBox={`0 0 ${W} ${height}`} width="100%" style={{ display: 'block' }}>
      {gridTicks.map((t, i) => (
        <g key={i}>
          <line x1={LEFT} x2={W - RIGHT} y1={yAt(t)} y2={yAt(t)} stroke="#1f2530" strokeWidth={1} />
          <text x={LEFT - 6} y={yAt(t) + 3} textAnchor="end" fontSize={10} fontFamily="ui-monospace, monospace" fill="#b7b1a3">
            {t}
          </text>
        </g>
      ))}
      <svg x={LEFT} y={TOP} width={plotW} height={plotH} viewBox={`0 0 ${plotW} ${plotH}`} preserveAspectRatio="none" overflow="visible">
        {series.map((s) => (
          <polyline
            key={s.label}
            points={linePoints(s.values, plotW, plotH, max)}
            fill="none"
            stroke={STROKE[s.tone]}
            strokeWidth={s.tone === 'amber' ? 2 : 1.5}
            strokeDasharray={s.dashed ? '4 4' : undefined}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      {series.map((s) =>
        s.values.map((v, i) => (
          <circle key={`${s.label}-${i}`} cx={xAt(i, s.values.length)} cy={yAt(v)} r={5} fill="transparent">
            <title>{`${s.label}${labels[i] ? ` · ${labels[i]}` : ''}: ${v}`}</title>
          </circle>
        )),
      )}
      {labels.map((l, i) =>
        i % step === 0 ? (
          <text key={`${l}-${i}`} x={xAt(i, labels.length)} y={height - 6} textAnchor="middle" fontSize={10} fontFamily="ui-monospace, monospace" fill="#b7b1a3">
            {l}
          </text>
        ) : null,
      )}
    </svg>
  )
}
